import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { BehaviorSubject } from 'rxjs';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NewSecurityScreeningModalComponent } from 'src/app/components/position-operations/clear-security-screening/new-security-screening-modal/new-security-screening-modal.component';
import { Items } from 'src/app/core/models/common/items.model';
import { TableColumn } from 'src/app/core/models/common/table-column.model';
import { UserService } from 'src/app/core/services/admin/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { DataUtils } from 'src/app/core/utils/data/data.utils';
import { IMAGE_SRC, NO_RESULTS, STATUS_OPTIONS } from '../../shared-constants';
import {
  ClearSecurityScreening,
  ColumnIds,
  ColumnText,
  CONST,
  EXCEL_FILE_NAME,
} from './clear-security-screening-table.constant';

@Component({
  selector: 'app-clear-security-screening-table',
  templateUrl: './clear-security-screening-table.component.html',
  styleUrls: ['./clear-security-screening-table.component.scss'],
  standalone: false,
})
export class ClearSecurityScreeningTableComponent implements OnInit {
  private userService = inject(UserService);
  private destroy = inject(DestroyRef);

  readonly SRC = IMAGE_SRC;
  readonly CONST = { ...CONST };
  readonly displayedColumns = Object.values(ColumnIds);
  readonly COLUMNTEXT = ColumnText;
  readonly NO_RESULTS = NO_RESULTS;
  readonly STATUS_OPTIONS = STATUS_OPTIONS;
  readonly IMAGE = IMAGE_SRC;

  dataSource = new MatTableDataSource<ClearSecurityScreening>();

  // Create admin table columns
  trackingNumberData = this.createAdminTableColumn(ColumnIds.TRACKING_NUMBER);
  screenerIdData = this.createAdminTableColumn(ColumnIds.SCREENER_ID);
  screeningTypeData = this.createAdminTableColumn(ColumnIds.SCREENING_TYPE);
  screeningMethodData = this.createAdminTableColumn(ColumnIds.SCREENING_METHOD);
  secondaryScreeningData = this.commonUtils.createTableColumn(
    ColumnIds.SECONDARY_SCREENING,
  );

  screeningTypeOption: Items[] = [];
  screeningMethodOption: Items[] = [];

  resetSelectDropdowns = new BehaviorSubject<boolean>(false);
  lastSubmission: ClearSecurityScreening | null = null;

  dropdownOpen = [false, false, false];

  // Create column search objects
  createAdminTableColumn(columnId: string): TableColumn {
    return {
      itemId: columnId,
      ids: [],
      formControl: new FormControl(''),
    };
  }

  constructor(
    public commonUtils: CommonUtils,
    private dialog: MatDialog,
    private dataUtils: DataUtils,
  ) {}

  /**
   * Initialize Component and create table filter
   *
   * @memberof ClearSecurityScreeningTableComponent
   */
  ngOnInit(): void {
    this.setTableData();
    this.setTableFilterOption();
    this.dataSource.filterPredicate = this.commonUtils.createFilter();
    this.subscribes();
  }

  /**
   * used to subscribes async data from service
   *
   * @memberof ClearSecurityScreeningTableComponent
   */
  subscribes() {
    this.dataUtils.newSecurityScreen$
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((resp) => {
        if (resp && resp?.newSecurityScreen) {
          this.addSecurityScreen(resp.newSecurityScreen);
        }
      });

    this.userService
      .getCurrentUser$()
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe();
  }

  resetState() {
    this.dataSource.data = [];
    this.screenerIdData.formControl.setValue('');
    this.clearAllFilters();
  }

  /**
   * Used to set table data
   *
   * @memberof ClearSecurityScreeningTableComponent
   */
  setTableData() {
    this.dataSource.data = [];
  }

  setTableFilterOption() {
    this.dataSource.data.forEach((item) => {
      const isScreeningTypeAdded = this.screeningTypeOption.find(
        (i) => i.id === item.screeningType,
      );
      if (!isScreeningTypeAdded) {
        this.screeningTypeOption.push({
          id: item.screeningType,
          selected: true,
        });
      }

      const isScreeningMethodAdded = this.screeningMethodOption.find(
        (i) => i.id === item.screeningMethod,
      );
      if (!isScreeningMethodAdded) {
        this.screeningMethodOption.push({
          id: item.screeningMethod,
          selected: true,
        });
      }
    });
  }

  /**
   * Filtering through the search bar
   *
   * @memberof ClearSecurityScreeningTableComponent
   */
  search() {
    this.commonUtils.searchFilter(
      [this.trackingNumberData, this.screenerIdData],
      this.dataSource,
      [
        this.screeningTypeData,
        this.screeningMethodData,
        this.secondaryScreeningData,
      ],
    );
  }

  /**
   * Triggers when a dropdown option is checked/unchecked
   *
   * @param {*} $event
   * @param {AdminTableColumn} field
   * @memberof ClearSecurityScreeningTableComponent
   */
  onStatusChangeForDropdowns(
    $event: string | boolean | unknown[],
    field: TableColumn,
  ) {
    field.ids[0] = $event as unknown as Items;
    switch (field.itemId) {
      case ColumnIds.SCREENING_TYPE:
        this.screeningTypeData.formControl.setValue($event);
        break;
      case ColumnIds.SCREENING_METHOD:
        this.screeningMethodData.formControl.setValue($event);
        break;

      default:
        break;
    }

    this.search();
  }

  /**
   * Clear all the filters if user has searched/filtered any column
   *
   * @memberof ClearSecurityScreeningTableComponent
   */
  clearAllFilters() {
    this.commonUtils.clearAllFilters(this.dataSource, this.dataSource.data, [
      this.trackingNumberData.formControl,
      this.screeningTypeData.formControl,
      this.screeningMethodData.formControl,
      this.screenerIdData.formControl,
      this.secondaryScreeningData.formControl,
    ]);
    this.resetSelectDropdowns.next(true);
  }

  /**
   * Export table data in excel file format
   *
   * @memberof ClearSecurityScreeningTableComponent
   */
  exportExcel(): void {
    this.commonUtils.exportExcel(
      Object.values(ColumnText),
      EXCEL_FILE_NAME,
      this.dataSource.filteredData,
    );
  }

  /**
   * open new SecurityScreen modal
   *
   * @memberof ClearSecurityScreeningTableComponent
   */
  newSecurityScreen() {
    this.dialog
      .open(NewSecurityScreeningModalComponent, {
        width: '34.25rem',
        disableClose: true,
        autoFocus: false,
        data: this.lastSubmission
          ? {
              screeningMethod: this.lastSubmission.screeningMethod,
            }
          : null,
      })
      .afterClosed()
      .subscribe((resp) => {
        if (resp && resp?.newSecuirtyScreen) {
          this.lastSubmission = resp?.newSecuirtyScreen;
          this.addSecurityScreen(resp.newSecuirtyScreen);
        }
      });
  }

  /**
   * Add new record to table
   *
   * @param {ClearSecurityScreening} clearSecurityScreening
   * @memberof ClearSecurityScreeningTableComponent
   */
  addSecurityScreen(clearSecurityScreening: ClearSecurityScreening) {
    const tableData = this.dataSource.data;

    tableData.push(clearSecurityScreening);
    this.dataSource.data = [...tableData];
    this.setTableFilterOption();
  }

  /**
   * Figure out if filter button is enabled
   * Should not be enabled if user has not filtered any column
   *
   * @readonly
   * @memberof ClearSecurityScreeningTableComponent
   */
  get isFilterEnabled() {
    return (
      this.trackingNumberData.formControl.value !== '' ||
      this.screeningTypeData.formControl.value !== '' ||
      this.screeningMethodData.formControl.value !== '' ||
      this.screenerIdData.formControl.value !== '' ||
      this.secondaryScreeningData.ids.length > 0
    );
  }
}
